import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import StatTile from "@/components/StatTile";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { api, fmtINR, fmtDateTime } from "@/lib/api";
import {
  IndianRupee,
  Receipt,
  Coins,
  Clock,
  Users,
  TrendingUp,
  ArrowRight,
  Activity,
  UserCheck,
  Globe,
  Download,
} from "lucide-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const formatShortDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
  } catch {
    return iso;
  }
};

export default function AdminDashboard() {
  const [kpis, setKpis] = useState(null);
  const [trend, setTrend] = useState([]);
  const [agents, setAgents] = useState([]);
  const [recent, setRecent] = useState([]);
  const [range, setRange] = useState(14);

  const load = (days = range) => {
    api.get("/admin/kpis").then((r) => setKpis(r.data)).catch(() => {});
    api.get(`/admin/sales-trend?days=${days}`).then((r) => setTrend(r.data)).catch(() => {});
    api.get("/admin/agents").then((r) => setAgents(r.data)).catch(() => {});
    api.get("/admin/memberships?limit=8").then((r) => setRecent(r.data)).catch(() => {});
  };

  useEffect(() => {
    load(range);
    // eslint-disable-next-line
  }, [range]);

  const top = [...agents].sort((a, b) => b.cards_sold - a.cards_sold).slice(0, 5);

  const exportLeaderboardCSV = () => {
    const ranked = [...agents].sort((a, b) => b.cards_sold - a.cards_sold);
    const head = ["Rank", "Agent", "Username", "Commission rate", "Cards sold", "Total commission"];
    const csv = [head.join(",")]
      .concat(
        ranked.map((ag, idx) =>
          [
            idx + 1,
            JSON.stringify(ag.agent_name || ""),
            ag.agent_username,
            ag.commission_rate,
            ag.cards_sold,
            ag.total_commission,
          ].join(",")
        )
      )
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `magicdil-leaderboard-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <DashboardLayout>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-primary mb-1.5">
            Admin overview
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-foreground tracking-tight">
            Sales & commission at a glance
          </h1>
        </div>
        <div className="flex items-center gap-1 rounded-lg border border-border bg-background p-1">
          {[7, 14, 30].map((d) => (
            <button
              key={d}
              onClick={() => setRange(d)}
              className={
                "px-3 py-1.5 rounded-md text-xs font-medium transition-colors " +
                (range === d
                  ? "bg-gradient-primary text-primary-foreground shadow-soft"
                  : "text-muted-foreground hover:text-foreground")
              }
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatTile icon={Receipt} label="Total cards sold" value={kpis?.total_sales ?? "—"} hint={`${kpis?.sales_today ?? 0} today · ${kpis?.sales_this_month ?? 0} this month`} accent="primary" />
        <StatTile icon={IndianRupee} label="Total revenue" value={fmtINR(kpis?.total_revenue)} hint="₹100 per card" accent="accent" />
        <StatTile icon={Coins} label="Total commission" value={fmtINR(kpis?.total_commission)} hint={`Paid: ${fmtINR(kpis?.paid_commission)}`} accent="success" />
        <StatTile icon={Clock} label="Pending payout" value={fmtINR(kpis?.pending_commission)} hint={`${kpis?.active_agents ?? 0} active agents`} accent="warning" />
      </div>

      <div className="mt-4 grid sm:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/6 to-accent/6 p-5 shadow-soft">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">Agent Sales</div>
              <div className="font-display font-bold text-2xl text-foreground mt-0.5">
                {kpis?.agent_sales_count ?? "—"} <span className="text-base font-medium text-muted-foreground">cards</span>
              </div>
            </div>
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-primary/12 text-primary">
              <UserCheck className="h-5 w-5" />
            </span>
          </div>
          <div className="text-sm text-foreground">
            Revenue <span className="font-semibold">{fmtINR(kpis?.agent_sales_revenue)}</span>
            <span className="mx-1.5 text-muted-foreground">·</span>
            commission <span className="font-semibold text-success">{fmtINR(kpis?.agent_commission)}</span>
          </div>
          <div className="mt-1 text-xs text-muted-foreground">Sold by your agents · commission applies.</div>
        </div>

        <div className="rounded-2xl border border-accent/20 bg-gradient-to-br from-accent/6 to-success/6 p-5 shadow-soft">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-accent">Direct Sales (Domain)</div>
              <div className="font-display font-bold text-2xl text-foreground mt-0.5">
                {kpis?.direct_sales_count ?? "—"} <span className="text-base font-medium text-muted-foreground">cards</span>
              </div>
            </div>
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-accent/12 text-accent">
              <Globe className="h-5 w-5" />
            </span>
          </div>
          <div className="text-sm text-foreground">
            Revenue <span className="font-semibold">{fmtINR(kpis?.direct_sales_revenue)}</span>
            <span className="mx-1.5 text-muted-foreground">·</span>
            <span className="font-semibold text-success">100% margin</span>
          </div>
          <div className="mt-1 text-xs text-muted-foreground">Customers purchased on the public site · no commission paid out.</div>
        </div>
      </div>

      <div className="mt-6 grid lg:grid-cols-3 gap-5">
        <Card className="lg:col-span-2 border-border/70 shadow-soft">
          <CardContent className="p-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                  Sales trend
                </div>
                <div className="font-display font-semibold text-lg text-foreground mt-0.5">
                  Daily cards · last {range} days
                </div>
              </div>
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <TrendingUp className="h-4 w-4" />
              </span>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trend.map((t) => ({ ...t, label: formatShortDate(t.date) }))} margin={{ top: 8, right: 10, bottom: 0, left: -16 }}>
                  <defs>
                    <linearGradient id="areaPrimary" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.45} />
                      <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{
                      background: "hsl(var(--popover))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: 12,
                      fontSize: 12,
                      boxShadow: "0 8px 24px -8px hsl(var(--primary) / 0.25)",
                    }}
                    labelStyle={{ fontWeight: 600, color: "hsl(var(--foreground))" }}
                    formatter={(v, k) => (k === "cards" ? [`${v} cards`, "Cards"] : [fmtINR(v), "Revenue"])}
                  />
                  <Area type="monotone" dataKey="cards" stroke="hsl(var(--primary))" strokeWidth={2.5} fill="url(#areaPrimary)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/70 shadow-soft">
          <CardContent className="p-5 sm:p-6 flex flex-col h-full">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                  Leaderboard
                </div>
                <div className="font-display font-semibold text-lg text-foreground mt-0.5">
                  Top agents
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-9"
                  onClick={exportLeaderboardCSV}
                  disabled={agents.length === 0}
                  title="Export leaderboard CSV"
                >
                  <Download className="h-4 w-4" />
                </Button>
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-accent/10 text-accent">
                  <Users className="h-4 w-4" />
                </span>
              </div>
            </div>
            {top.length === 0 ? (
              <div className="py-8 text-center text-muted-foreground text-sm">
                No agents yet. <Link to="/admin/agents" className="text-primary font-semibold">Add one</Link>.
              </div>
            ) : (
              <ul className="space-y-3">
                {top.map((a, idx) => (
                  <li key={a.agent_id} className="flex items-center gap-3">
                    <span
                      className={
                        "inline-flex h-9 w-9 items-center justify-center rounded-xl text-sm font-bold " +
                        (idx === 0
                          ? "bg-gradient-primary text-primary-foreground"
                          : "bg-muted text-foreground")
                      }
                    >
                      {idx + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm text-foreground truncate">{a.agent_name}</div>
                      <div className="text-xs text-muted-foreground">@{a.agent_username} · {a.commission_rate}%</div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-sm text-foreground">{a.cards_sold}</div>
                      <div className="text-[11px] text-success">{fmtINR(a.total_commission)}</div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <div className="mt-auto pt-5">
              <Link to="/admin/agents">
                <Button variant="outline" className="w-full">
                  Manage agents <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="mt-5 border-border/70 shadow-soft">
        <CardContent className="p-5 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                Recent activity
              </div>
              <div className="font-display font-semibold text-lg text-foreground mt-0.5">
                Latest cards sold
              </div>
            </div>
            <Link to="/admin/sales" className="text-sm font-medium text-primary inline-flex items-center gap-1 hover:underline">
              View all <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          {recent.length === 0 ? (
            <div className="py-10 text-center">
              <Activity className="h-7 w-7 mx-auto text-primary/60 mb-2" />
              <p className="text-sm text-muted-foreground">No sales yet. Create an agent and start selling.</p>
            </div>
          ) : (
            <div className="overflow-x-auto -mx-5 sm:-mx-6">
              <table className="w-full text-sm">
                <thead className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                  <tr className="border-b border-border">
                    <th className="text-left font-semibold px-5 sm:px-6 py-3">Patient</th>
                    <th className="text-left font-semibold py-3">Membership #</th>
                    <th className="text-left font-semibold py-3">Agent</th>
                    <th className="text-left font-semibold py-3">When</th>
                    <th className="text-right font-semibold px-5 sm:px-6 py-3">Commission</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {recent.map((m) => (
                    <tr key={m.id} className="hover:bg-muted/40">
                      <td className="px-5 sm:px-6 py-3">
                        <div className="font-medium text-foreground">{m.patient_name}</div>
                        <div className="text-xs text-muted-foreground">+91 {m.patient_mobile}</div>
                      </td>
                      <td className="py-3 font-mono-card text-xs tracking-wider text-foreground">
                        {m.membership_number}
                      </td>
                      <td className="py-3 text-muted-foreground">
                        {m.agent_name ? (
                          <span className="text-foreground">{m.agent_name}<span className="text-muted-foreground"> · @{m.agent_username}</span></span>
                        ) : <span className="text-muted-foreground italic">walk-in</span>}
                      </td>
                      <td className="py-3 text-muted-foreground">{fmtDateTime(m.issued_at)}</td>
                      <td className="px-5 sm:px-6 py-3 text-right">
                        <div className="font-semibold text-foreground">{fmtINR(m.commission_amount)}</div>
                        {m.commission_amount > 0 && (
                          <Badge className={
                            "mt-1 text-[10px] " +
                            (m.commission_paid
                              ? "bg-success/12 text-success border border-success/25 hover:bg-success/12"
                              : "bg-warning/15 text-warning-foreground border border-warning/30 hover:bg-warning/15")
                          }>{m.commission_paid ? "Paid" : "Pending"}</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </DashboardLayout>
  );
}
